import { Component } from "@theme/component";
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
} from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty(
      "--header-height",
      `${roundedHeaderHeight}px`,
    );

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }

    this.#updateShrunkWidthDebounced();
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        if (this.dataset.themeColor)
          changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #lastWidth = 0;
  /** @type {number | null} */
  #initTimeout = null;

  #measureShrunkWidth = () => {
    // Briefly force the measurement state (unconstrained)
    this.dataset.measuringPill = "";

    requestAnimationFrame(() => {
      const columns = /** @type {HTMLElement | null} */ (
        this.querySelector(".header__columns")
      );

      if (columns) {
        // Measure the natural width of the columns container
        // We use getBoundingClientRect for sub-pixel precision.
        // Note: rect.width already includes the padding from .header__columns
        const rect = columns.getBoundingClientRect();

        // Use a tighter wrap on mobile, but keep a comfortable buffer on desktop
        const isMobile = window.innerWidth < 990;
        const buffer = isMobile ? 16 : 90;

        let shrunkWidth = Math.ceil(rect.width) + buffer;

        // Cap the width to viewport on mobile to prevent overflow
        const maxWidth = window.innerWidth * 0.95;
        if (shrunkWidth > maxWidth && window.innerWidth < 990) {
          shrunkWidth = maxWidth;
        }

        this.style.setProperty("--shrunk-width", `${Math.ceil(shrunkWidth)}px`);
      }

      delete this.dataset.measuringPill;
    });
  };

  #updateShrunkWidthDebounced = () => {
    if (Math.abs(this.#lastWidth - window.innerWidth) < 10) return;
    this.#lastWidth = window.innerWidth;

    if (this.#initTimeout) clearTimeout(this.#initTimeout);
    this.#initTimeout = setTimeout(this.#measureShrunkWidth, 150);
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute("sticky");
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    // Fix: Use a small threshold so the browser reliably detects the top of page
    // even when scroll events stop a few pixels before scrollTop reaches exactly 0
    const isAtTop = scrollTop <= 5;

    // ALways update scroll direction so CSS animations trigger reliably
    if (isAtTop) {
      this.dataset.scrollDirection = "none";
    } else if (isScrollingUp) {
      this.dataset.scrollDirection = "up";
    } else {
      this.dataset.scrollDirection = "down";
    }

    if (!this.#offscreen && stickyMode !== "always") {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = "active";
      }
    } else {
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);

    // Initial measurement
    requestAnimationFrame(() => {
      this.#lastWidth = window.innerWidth;
      this.#measureShrunkWidth();
    });

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    document.removeEventListener("scroll", this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

class HeaderSearchInline extends HTMLElement {
  constructor() {
    super();
    this.searchButton = this.querySelector(".header-search-inline__action");
    this.inputWrapper = this.querySelector(
      ".header-search-inline__input-wrapper",
    );
    this.clearButton = this.querySelector(".header-search-inline__clear");

    /** @type {HTMLInputElement | null} */
    this.input = this.querySelector(".header-search-inline__input");

    if (this.searchButton) {
      this.searchButton.addEventListener("click", this.openSearch.bind(this));
    }

    if (this.clearButton) {
      this.clearButton.addEventListener("click", this.closeSearch.bind(this));
    }
  }

  /**
   * @param {Event} event
   */
  openSearch(event) {
    event.preventDefault();

    if (this.inputWrapper) {
      this.inputWrapper.classList.add("is-active");
    }
    if (this.clearButton) {
      this.clearButton.classList.remove("hidden");
    }
    if (this.searchButton) {
      this.searchButton.classList.add("hidden");
    }
    if (this.input) {
      this.input.focus();
    }
  }

  closeSearch() {
    if (this.inputWrapper) {
      this.inputWrapper.classList.remove("is-active");
    }
    if (this.clearButton) {
      this.clearButton.classList.add("hidden");
    }
    if (this.searchButton) {
      this.searchButton.classList.remove("hidden");
    }
  }
}

if (!customElements.get("header-search-inline")) {
  customElements.define("header-search-inline", HeaderSearchInline);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute("transparent") &&
            header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty(
        "--header-group-height",
        `${roundedHeaderGroupHeight}px`,
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
